import { useContext, useEffect } from 'react'

import { useAuth0 } from '@auth0/auth0-react'
import { JOBS_WATCHING_INTERVAL } from '@editor/config/constants'
import { GlobalModalContext } from '@editor/contexts/GlobalModal'
import { FileProcessingStatus } from '@editor/interfaces/file'
import { InspectionArea } from '@editor/interfaces/inspection'
import { Project } from '@editor/interfaces/project'
import { setDisplaySettings } from '@editor/pages/projects/editor/store/editorSettings'
import { getInspectionAreas, setInspectionAreasOrder } from '@editor/services/InspectionArea'
import { getProject } from '@editor/services/Projects'
import { isInvited, isOwner } from '@editor/services/Validation'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import {
  fetchInspectionArea,
  reset,
  setInitComplete,
  setInspectionArea,
  setInspectionAreas,
  setIsInvited,
  setIsLoading,
  setIsOwner,
  setProject,
} from '@editor/store/page'
import * as Sentry from '@sentry/react'
import { useLocation, useParams } from 'react-router'

export const usePage = () => {
  const { project_id } = useParams<{ project_id: string }>()
  const location = useLocation()
  const queries = new URLSearchParams(location.search)
  const inspection_area_id = queries.get('area')

  // Context
  const { getAccessTokenSilently } = useAuth0()
  const { showErrorModal } = useContext(GlobalModalContext)

  // Store
  const dispatch = useAppDispatch()
  const initComplete = useAppSelector((state) => state.page.initComplete)
  const project = useAppSelector((state) => state.page.project)
  const inspectionArea = useAppSelector((state) => state.page.inspectionArea)
  const areaSwitched = useAppSelector((state) => state.page.areaSwitched)
  const userLoaded = useAppSelector((state) => state.user.userLoaded)
  const userProfile = useAppSelector((state) => state.user.userProfile)

  /**
   * Check the status of the inspection area
   */
  useEffect(() => {
    if (inspectionArea?.downsample_status?.status === FileProcessingStatus.RUNNING) {
      const checkInspectionAreaStatus = async () => {
        try {
          if (!project_id || !inspectionArea.inspection_area_id) return

          const token = await getAccessTokenSilently()
          if (!token) return

          const area = await dispatch(
            fetchInspectionArea({
              access_token: token,
              project_id,
              inspection_area_id: inspectionArea.inspection_area_id,
            }),
          ).unwrap()

          if (area?.downsample_status?.status === FileProcessingStatus.RUNNING) {
            setTimeout(() => {
              void checkInspectionAreaStatus()
            }, JOBS_WATCHING_INTERVAL)
          }
        } catch (error) {
          Sentry.captureException(error)
          showErrorModal('点群ファイルの変換に失敗しました。')
        }
      }

      setTimeout(() => {
        void checkInspectionAreaStatus()
      }, JOBS_WATCHING_INTERVAL)
    }
  }, [
    project_id,
    inspectionArea?.inspection_area_id,
    inspectionArea?.downsample_status?.status,
    showErrorModal,
    getAccessTokenSilently,
    dispatch,
  ])

  /**
   * If area was switched after initial page load.
   */
  useEffect(() => {
    if (!initComplete || !areaSwitched?.to) return

    dispatch(setIsLoading(false))
    dispatch(setInspectionArea(areaSwitched.to))
    if (areaSwitched.to.display_config) dispatch(setDisplaySettings(areaSwitched.to.display_config))

    setTimeout(() => document.dispatchEvent(new CustomEvent('hooks:page:completed')))
  }, [initComplete, areaSwitched?.to, showErrorModal, dispatch])

  /**
   * Fetch project and inspection area on page load.
   */
  useEffect(() => {
    if (!userLoaded || !project_id || initComplete) {
      return () => null
    }

    dispatch(setIsLoading(true))

    const controller = new AbortController()

    void (async () => {
      const token = await getAccessTokenSilently()
      if (!token) {
        dispatch(setIsLoading(false))
        return
      }

      // fetch project and all inspection areas of that project
      const result = await Promise.all([
        getProject(token, project_id, showErrorModal, controller.signal),
        getInspectionAreas(token, project_id, showErrorModal, controller.signal),
      ])

      // false refers to aborted requests, ignore them
      if (result.some((r) => r === false)) {
        return
      }

      if (result.some((r) => r === null)) {
        dispatch(setIsLoading(false))
        return
      }

      // Derive inspection area from areas list, if inspection_area_id is provided
      const data = {
        project: result[0] as Project,
        inspectionAreas: setInspectionAreasOrder(result[1] as InspectionArea[], []).sort((a, b) => a.order! - b.order!),
        inspectionArea:
          (result[1] as InspectionArea[])?.find((ia) => ia.inspection_area_id === inspection_area_id) || null,
      }

      dispatch(setProject(data.project))
      dispatch(setInspectionAreas(data.inspectionAreas))
      dispatch(setIsLoading(false))
      dispatch(setInitComplete())

      // This hook is also used in a non-inspection area context such as project-level inspection sheet
      // In such cases, inspectionArea would be null
      if (data.inspectionArea) {
        dispatch(setInspectionArea(data.inspectionArea))
        if (data.inspectionArea.display_config) dispatch(setDisplaySettings(data.inspectionArea.display_config))
      }

      setTimeout(() => document.dispatchEvent(new CustomEvent('hooks:page:completed')))
    })()

    return () => {
      controller.abort()
    }
  }, [userLoaded, project_id, inspection_area_id, initComplete, getAccessTokenSilently, dispatch, showErrorModal])

  /**
   * Set owner and invited permission.
   */
  useEffect(() => {
    if (!project || !userProfile) return

    dispatch(setIsOwner(isOwner(project, userProfile)))
    dispatch(setIsInvited(isInvited(project, userProfile)))
  }, [project, userProfile, dispatch])

  /**
   * Cleanup on unmount.
   */
  useEffect(
    () => () => {
      dispatch(reset())
    },
    [dispatch],
  )

  return {
    project,
    inspectionArea,
  }
}
